import type { Role, User } from '@/types'
import { canAccessGestoresModule } from '@/config/gestorAccess'
import {
  isPathAllowedForInstance,
  isRocketInstance,
  type InstanceProfile,
  INSTANCE_PROFILE,
} from '@/config/instanceProfile'
import { ROLE_REDIRECTS } from '@/utils/loginSchemas'
import { canAccessEmpresasContatos } from '@/utils/userEventMode'

/** Rotas que gestores (MANAGER) não acessam — operação via Portal Gestores / CS / Tarefas. */
export const MANAGER_BLOCKED_PATH_PREFIXES = [
  '/dashboard',
  '/bdr-panel',
  '/deals',
  '/closer-leads',
  '/rocket-search',
  '/empresas',
  '/partners',
  '/financeiro',
  '/clientes',
  '/pj-agrada',
  '/proposal-studio',
  '/security/bots',
  '/configuracao',
] as const

export function isManagerBlockedPath(
  pathname: string,
  profile: InstanceProfile = INSTANCE_PROFILE,
): boolean {
  const path = pathname.split('?')[0]
  // Na Rocket, gestor/closer opera o comercial + Configuração.
  // CS, gestores e security continuam fora via ROCKET_BLOCKED_PATH_PREFIXES.
  if (profile === 'rocket') {
    return false
  }
  return MANAGER_BLOCKED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

export function canManagerAccessPath(
  pathname: string,
  user: Pick<User, 'role' | 'setor'> | null | undefined,
): boolean {
  if (!user || normalizeRole(user.role) !== 'manager') return true
  const path = pathname.split('?')[0]
  if (isManagerBlockedPath(path)) return false
  if (path === '/gestores' || path.startsWith('/gestores/')) {
    return canAccessGestoresModule(user)
  }
  return true
}

/** Rotas exclusivas do portal PJ (usuario_agrada). */
export const PJ_ONLY_ROUTES = ['/portal'] as const
export const INDICADOR_ONLY_ROUTES = ['/portal-indicacoes', '/parceiros'] as const
export const INDICADOR_PATH_PREFIXES = ['/portal-indicacoes', '/parceiros'] as const
export const INDICADOR_ROLE: Role = 'indicador'

/** Rotas permitidas ao papel CS (sem financeiro/comercial amplo). */
export const CS_ALLOWED_PREFIXES = ['/cs', '/tarefas'] as const

export const PJ_ROLE: Role = 'usuario_agrada'
export const CS_ROLE: Role = 'cs'

/** Papéis do CRM que também acessam /portal (menu + rota), sem ficar presos só ao portal. */
export const PORTAL_CRM_ROLES: Role[] = [
  'admin',
  'manager',
  'closer',
  'bdr',
  'financeiro',
]

export const PORTAL_ACCESS_ROLES: Role[] = [...PORTAL_CRM_ROLES, PJ_ROLE]

/** Papéis comerciais — na Rocket o gestor também opera o funil. */
const COMMERCIAL_ROLES: Role[] = isRocketInstance
  ? ['admin', 'closer', 'bdr', 'manager']
  : ['admin', 'closer', 'bdr']

/** Papéis permitidos por rota (alinhado ao menu e à API). */
export const ROUTE_ROLES: Record<string, Role[] | undefined> = {
  '/dashboard': isRocketInstance ? ['admin', 'closer', 'manager'] : ['admin', 'closer'],
  '/bdr-panel': ['admin', 'bdr', 'closer'],
  '/deals': COMMERCIAL_ROLES,
  '/tarefas': ['admin', 'manager', 'closer', 'bdr', 'cs'],
  '/closer-leads': COMMERCIAL_ROLES,
  '/empresas': COMMERCIAL_ROLES,
  '/partners': isRocketInstance ? ['admin', 'closer', 'manager'] : ['admin', 'closer'],
  '/indicacoes': isRocketInstance ? ['admin', 'closer', 'manager'] : ['admin', 'closer'],
  '/partners/promocoes': ['admin', 'closer', 'manager'],
  '/portal-indicacoes': ['indicador'],
  '/rocket-search': ['admin', 'bdr', 'closer'],
  '/proposal-studio': ['admin', 'closer', 'bdr'],
  '/apresentacao': COMMERCIAL_ROLES,
  '/configuracao': isRocketInstance ? ['admin', 'manager'] : ['admin'],
  '/financeiro': ['admin', 'financeiro'],
  '/financeiro/fila': ['admin', 'financeiro'],
  '/financeiro/solicitacoes': ['admin', 'financeiro', 'closer', 'bdr'],
  '/portal': PORTAL_ACCESS_ROLES,
  '/clientes': ['admin', 'financeiro', 'closer'],
  '/clientes/novo': ['admin', 'financeiro'],
  '/pj-agrada': ['admin', 'financeiro'],
  '/gestores': ['admin', 'manager'],
  '/security/bots': ['admin'],
  '/cs': ['admin', 'manager', 'cs'],
  '/cs/carteira': ['admin', 'manager', 'cs'],
  '/cs/retencao': ['admin', 'manager', 'cs'],
}

export function normalizeRole(role: string): Role {
  const r = role.toLowerCase()
  if (r === 'pos_venda') return 'cs'
  return r as Role
}

export function isPjRole(role: string): boolean {
  return normalizeRole(role) === PJ_ROLE
}

export function isCsRole(role: string): boolean {
  return normalizeRole(role) === CS_ROLE
}

export function isPjAllowedPath(path: string): boolean {
  const pathname = path.split('?')[0]
  return (PJ_ONLY_ROUTES as readonly string[]).includes(pathname)
}

export function isIndicadorRole(role: string): boolean {
  return normalizeRole(role) === INDICADOR_ROLE
}

export function isIndicadorAllowedPath(path: string): boolean {
  const pathname = path.split('?')[0]
  return INDICADOR_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function isCsAllowedPath(path: string): boolean {
  const pathname = path.split('?')[0]
  return CS_ALLOWED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function canAccessRoute(
  role: string,
  path: string,
  user?: Pick<User, 'role' | 'setor' | 'eventModeActive'> | null,
): boolean {
  const pathname = path.split('?')[0]
  const normalized = normalizeRole(role)

  if (!isPathAllowedForInstance(pathname)) {
    return false
  }

  if (pathname === '/empresas' && !canAccessEmpresasContatos(user)) {
    return false
  }

  if (normalized === INDICADOR_ROLE) {
    return isIndicadorAllowedPath(pathname)
  }

  if (normalized === CS_ROLE) {
    return isCsAllowedPath(pathname)
  }

  if (normalized === 'manager' && !canManagerAccessPath(pathname, user ?? { role: 'manager', setor: undefined })) {
    return false
  }

  if (pathname.startsWith('/gestores/chamados/')) {
    return ['admin', 'manager'].includes(normalized)
  }

  if (pathname.startsWith('/gestores/entradas/')) {
    return ['admin', 'manager'].includes(normalized)
  }

  const allowed = ROUTE_ROLES[pathname]
  if (!allowed) {
    if (pathname.startsWith('/cs/')) return ['admin', 'manager', 'cs'].includes(normalized)
    if (normalized === 'manager' && isManagerBlockedPath(pathname)) return false
    return true
  }
  return allowed.includes(normalized)
}

const MANAGER_HOME_CANDIDATES = ['/gestores', '/tarefas', '/cs/carteira', '/portal'] as const

export function getRoleFallbackPath(
  role: string,
  currentPath?: string,
  user?: Pick<User, 'role' | 'setor'> | null,
): string {
  const normalized = normalizeRole(role)
  if (normalized === INDICADOR_ROLE) return '/parceiros/home'

  if (isRocketInstance) {
    const rocketHome =
      normalized === 'admin'
        ? (['/dashboard', '/deals', '/empresas', '/tarefas', '/configuracao'] as const)
        : (['/deals', '/dashboard', '/empresas', '/tarefas', '/configuracao'] as const)
    for (const path of rocketHome) {
      if (path !== currentPath && canAccessRoute(normalized, path, user)) {
        return path
      }
    }
    return normalized === 'admin' ? '/dashboard' : '/deals'
  }

  if (normalized === 'manager') {
    for (const path of MANAGER_HOME_CANDIDATES) {
      if (path !== currentPath && canAccessRoute(normalized, path, user)) {
        return path
      }
    }
    return '/tarefas'
  }

  const preferred = ROLE_REDIRECTS[normalized] ?? '/deals'

  if (canAccessRoute(normalized, preferred, user)) {
    return preferred
  }

  for (const [path, roles] of Object.entries(ROUTE_ROLES)) {
    if (roles?.includes(normalized) && path !== currentPath && canAccessRoute(normalized, path, user)) {
      return path
    }
  }

  if (normalized === CS_ROLE) return '/cs/carteira'

  return '/deals'
}

/** Itens de navegação com restrição opcional por papel */
export type NavItemAccess = {
  to: string
  roles?: Role[]
}

export function canSeeNavItem(
  role: string,
  item: NavItemAccess,
  user?: Pick<User, 'role' | 'setor' | 'eventModeActive'> | null,
): boolean {
  const path = item.to.split('?')[0]
  if (!isPathAllowedForInstance(path)) return false
  if (isRocketInstance && item.to === '/security/bots') return false
  if (item.roles?.length && !item.roles.includes(normalizeRole(role))) return false
  return canAccessRoute(role, path, user ?? { role: normalizeRole(role), setor: undefined })
}

export function canAccessPortal(role: string): boolean {
  return PORTAL_ACCESS_ROLES.includes(normalizeRole(role))
}
